import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { adminRoutePaths, globalSearchKinds } from "../adminRoutes";
import CaseSearch from "../CaseSearch";
import OrderSearch from "../OrderSearch";
import { adminTokens } from "../theme/adminTokens";
import { PageHeader } from "../ui/PageHeader";
import { SegmentedControl, SegmentedControlOption } from "../ui/SegmentedControl";
import UserSearch from "../UserSearch";

const [, , spaceMd, spaceLg] = adminTokens.spacing;

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceLg}px;
  min-height: 100%;
  background-color: ${adminTokens.color.canvas};
  padding: ${spaceMd}px;
`;

type SearchKind = "user" | "case" | "order";

interface FindAddress {
  entry: string | null;
  key?: string;
}

const searches: {
  kind: SearchKind;
  label: string;
  content: (address: FindAddress) => JSX.Element;
}[] = [
  {
    kind: "user",
    label: "User",
    content: ({ entry, key }) => <UserSearch addressEntry={entry} entryKey={key} />,
  },
  { kind: "case", label: "Case", content: () => <CaseSearch /> },
  { kind: "order", label: "Order", content: () => <OrderSearch /> },
];

const panelId = (kind: SearchKind) => `find-panel-${kind}`;

const tabOptions: SegmentedControlOption[] = searches.map(({ kind, label }) => ({
  value: kind,
  label,
  panelId: panelId(kind),
}));

// FIND-R9: an address that names none of the three searches shows the User tab.
const selectedSearch = (searchParam: string | null) =>
  searches.find(({ kind }) => kind === searchParam) ?? searches[0];

// The Find screen (FIND-R5…R16): a header with the tab bar at its right, and the one search the
// selected tab names. The tab lives in the address, so a reload and the admin search land on it;
// choosing a tab adds a history entry and drops the entry typed for the previous search. What each
// search holds, and its result, is kept by the admin while another tab or screen is opened (FIND-R17).
const FindScreen = () => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const { kind, label, content } = selectedSearch(searchParams.get("search"));
  const entry = kind === globalSearchKinds.user ? searchParams.get("q") : null;

  const chooseTab = (nextKind: string) =>
    history.push(`${adminRoutePaths.find}?search=${nextKind}`);

  return (
    <Shell data-testid="admin-dashboard-v2-shell">
      <PageHeader
        title="Find"
        subtitle="Locate a User, their Cases and their Orders."
        aside={
          <SegmentedControl
            label="Search type"
            kind="tabs"
            options={tabOptions}
            value={kind}
            onChange={chooseTab}
          />
        }
      />
      <div role="tabpanel" id={panelId(kind)} aria-label={label}>
        {content({ entry, key: location.key })}
      </div>
    </Shell>
  );
};

export default FindScreen;
